import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import ModelSelect from "../ui/ModelSelect";
import Input from "../ui/Input";
import FrenchDatePicker from "../ui/FrenchDatePicker";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaTrash } from "react-icons/fa";
import { format, parseISO } from "date-fns";

// Custom CSS to fix datepicker opacity and z-index issues
const datepickerStyles = `
  .datepicker-popper {
    z-index: 99999 !important;
    opacity: 1 !important;
  }
  
  .react-datepicker-wrapper {
    z-index: 99999 !important;
  }
  
  .react-datepicker {
    z-index: 99999 !important;
    opacity: 1 !important;
  }
  
  /* Ensure the datepicker appears above everything but positioned correctly */
  .react-datepicker-popper {
    z-index: 99999 !important;
  }
  
  .react-datepicker-popper[data-placement^="bottom"] {
    margin-top: 8px !important;
  }
  
  .react-datepicker-popper[data-placement^="top"] {
    margin-bottom: 8px !important;
  }
`;

const ExportProduction = () => {
  const [modeles, setModeles] = useState([]);
  const [modeleSelectionne, setModeleSelectionne] = useState("");
  const [production, setProduction] = useState(0);
  const [productionTotale, setProductionTotale] = useState(0);
  const [encore, setEncore] = useState(0);
  const [entre, setEntre] = useState(0);
  const [dateSelectionnee, setDateSelectionnee] = useState(null);
  const [boutonDesactive, setBoutonDesactive] = useState(false);
  const [anciensExports, setAnciensExports] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExport, setSelectedExport] = useState(null);

  useEffect(() => {
    const fetchModeles = async () => {
      try {
        const response = await api.get("/models");
        setModeles(response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération des modèles :", error);
      }
    };

    fetchModeles();
  }, []);

  useEffect(() => {
    const fetchExportProduction = async () => {
      if (modeleSelectionne) {
        try {
          const responseControl = await api.get(
            `/magasin_production/${modeleSelectionne}`
          );
          setEntre(responseControl.data.value);
        } catch (error) {
          console.error("Erreur lors de la récupération des données de contrôle :", error);
        }

        try {
          const response = await api.get(`/export/${modeleSelectionne}`);
          setProductionTotale(response.data.total);
          setAnciensExports(response.data.exports); // Stocker les anciens exports
        } catch (error) {
          console.error("Erreur lors de la récupération des données d'ExportProduction :", error);
          setEncore(entre);
        }
      }
    };

    fetchExportProduction();
    setEncore(entre - productionTotale);
  }, [modeleSelectionne, entre, productionTotale]);

  const handleModeleChange = (e) => {
    setModeleSelectionne(e.target.value);
  };

  const handleDateChange = (date) => {
    setDateSelectionnee(date);
  };

  const onChange = (name, value) => {
    const nouvelleValeur = parseInt(value, 10) || 0;
    const nouvelEncore = entre - nouvelleValeur;

    if (nouvelEncore < 0) {
      toast.error("Encore ne peut pas être inférieur à 0");
      return;
    }

    setProduction(nouvelleValeur);
    setEncore(nouvelEncore);
  };

  const handleSave = async () => {
    if (encore < 0) {
      toast.error("Encore ne peut pas être inférieur à 0");
      return;
    }

    if (production == null) {
      toast.error("La valeur de la production est requise");
      return;
    }

    if (dateSelectionnee == null) {
      toast.error("La date est requise");
      return;
    }

    setBoutonDesactive(true);

    try {
      await api.post(`/export/${modeleSelectionne}`, {
        value: production,
        // This is a calendar date, so keep it in local time instead of converting it to UTC.
        date: format(dateSelectionnee, "yyyy-MM-dd"),
      });
      const response = await api.get(`/export/${modeleSelectionne}`);
      setProductionTotale(response.data.total);
      setAnciensExports(response.data.exports);
      toast.success("Données enregistrées avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour des données d'ExportProduction :", error);
      toast.error("Échec de la mise à jour des données de production.");
    } finally {
      setBoutonDesactive(false);
    }
  };

  const handleDelete = async (exportId) => {
    setSelectedExport(exportId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/export/${selectedExport}`);
      toast.success("Export supprimé avec succès");
      // Refresh the exports list
      const response = await api.get(`/export/${modeleSelectionne}`);
      setProductionTotale(response.data.total);
      setAnciensExports(response.data.exports);
    } catch (error) {
      console.error("Erreur lors de la suppression de l'export:", error);
      toast.error("Échec de la suppression de l'export");
    } finally {
      setShowDeleteModal(false);
      setSelectedExport(null);
    }
  };

  const modeleSelectionneData = modeles.find((modele) => modele.id == modeleSelectionne);
  const totalExports = anciensExports.reduce(
    (total, exportItem) => total + (Number(exportItem.value) || 0),
    0
  );

  return (
    <div className="pt-[5.5rem] pl-64 min-h-screen bg-gray-50">
      {/* Inject custom datepicker styles */}
      <style>{datepickerStyles}</style>
      
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Exportation</h2>
          <div className="w-64">
            <ModelSelect value={modeleSelectionne} onChange={handleModeleChange} models={modeles} />
          </div>
        </div>
        
        {modeleSelectionneData && (
          <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">{modeleSelectionneData.category}</h3>
              <h3 className="font-semibold text-gray-700">{modeleSelectionneData.client}</h3>
            </div>
          </div>
        )}
        
        {modeleSelectionneData && (
          <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">Encour : {encore}</h3>
              <h3 className="font-semibold text-gray-700">
                Total des exports : {totalExports}
              </h3>
            </div>
          </div>
        )}
      {modeleSelectionne && (
        <>
          <div className="flex justify-between items-center mx-auto mt-[2rem] mb-4 shadow-md max-w-[500px] p-5 rounded border">
            <div>
              <h3 className="font-semibold">Production</h3>
              <Input
                handleChange={(name, value) => onChange("production", value)}
                name={"production"}
                placeholder="Production totale"
                text={production ? production.toString() : "0"}
              />
            </div>
            <div className="font-semibold ml-5">
              <label className="block">Date d'exportation</label>
              <FrenchDatePicker
                selected={dateSelectionnee}
                onChange={handleDateChange}
                dateFormat="yyyy-MM-dd"
                className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
                popperClassName="datepicker-popper"
              />
            </div>
          </div>
          <div className="flex justify-center mt-4">
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              onClick={handleSave}
              disabled={boutonDesactive}
            >
              Enregistrer
            </button>
          </div>
          <div className="max-h-60 overflow-y-scroll border p-4 mt-4 rounded">
            <h3 className="font-semibold mb-2">Exports précédents</h3>
            <ul>
              {anciensExports.map((exportItem, index) => (
                <li key={index} className="mb-2 border-b pb-2 flex justify-between items-center">
                  <div>
                    <p>Date : {format(parseISO(exportItem.date), "dd/MM/yyyy")}</p>
                    <p>Valeur : {exportItem.value}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(exportItem.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
                <p>Êtes-vous sûr de vouloir supprimer cet export ?</p>
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default ExportProduction;
